/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useCallback, useState } from 'react';

// Mantine
import {
	Box,
	Flex,
	Image,
	NumberFormatter,
	Skeleton,
} from '@mantine/core';

// Styles
import classes from './KpiSection.module.css';

// UI local components
import { Select } from '../Select/Select';
import { Text } from '../Text';
import { Title } from '../Title';

// Icons
import {
	IconBuilding,
	IconCalendarWeek,
	IconClipboardList,
} from '@tabler/icons-react';

// Constants
import { ACLS } from '@config/constants/acls';
import {
	CONTACT_US_INUK_URL,
	NOT_APPLICABLE,
} from '@config/constants/common';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import { DatesRangeValue } from '@mantine/dates';
import { checkAclFront } from '@utils/acl-front';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { DatePickerInput } from '../DatePickerInput/DatePickerInput';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type TKpiSectionProps = {
	image: string;
	label: string;
	value?: string | number;
	unit?: string;
	dateRange?: string;
	color?: string;
	className?: string;
	withShadow?: boolean;
	withDate?: boolean;
};

type TCompanyOption = {
	label: string;
	value: string;
};

type KpiSectionProps = {
	data: TKpiSectionProps[];
	companyOptions: TCompanyOption[];
	onCompanyChange?: (company: string | null) => void;
	onDateChange: (value: DatesRangeValue<string>) => void;
	projectOptions?: TCompanyOption[];
	onProjectChange?: (company: string | null) => void;
	isLoading?: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

const KpiCard: React.FC<TKpiSectionProps> = ({
	image,
	label,
	value,
	unit,
	dateRange,
	color,
	className,
	withShadow,
	withDate,
}) => (
	<Box
		className={clsx(
			!withShadow && classes.containerKpi,
			className,
			withShadow && classes.withShadowBorder
		)}
	>
		<div className={clsx(classes.containerKpiContent)}>
			<Image
				src={image}
				alt={`${label} icon`}
				h={48}
				w={48}
				mb="8px"
				radius={'xl'}
			/>
			<Text
				c="var(--mantine-color-igluTextSecondary-9)"
				variant="subtitle1"
			>
				{label}
			</Text>
			<Flex align="center">
				<NumberFormatter
					className={classes.styleNumber}
					value={value ?? 0}
					decimalSeparator=","
					thousandSeparator=" "
				/>
				<Text
					c="var(--mantine-color-igluTextPrimary-9)"
					variant="body1"
					pl="10px"
				>
					{unit ?? ''}
				</Text>
			</Flex>
			{withDate && (
				<Flex align="center">
					<IconCalendarWeek
						color={
							color ??
							'var(--mantine-color-igluTextSecondary-9)'
						}
						size={18}
						stroke={1.5}
					/>
					<Text
						ml="6px"
						c="var(--mantine-color-igluTextSecondary-9)"
						variant="body2"
					>
						{dateRange ?? NOT_APPLICABLE}
					</Text>
				</Flex>
			)}
		</div>
	</Box>
);

export const KpiSection: React.FC<KpiSectionProps> = ({
	data,
	companyOptions,
	onCompanyChange,
	onDateChange,
	projectOptions,
	onProjectChange,
	isLoading,
}) => {
	const { checkRole, checkAcls, role } =
		useAuthorization();
	/* ******************************** STATES ******************************* */
	const [selectedCompany, setSelectedCompany] = useState<
		string | null
	>(null);
	const [selectedProject, setSelectedProject] = useState<
		string | null
	>(null);
	const { t } = useTranslation();

	const [selectedDates, setSelectedDates] = useState<any>(
		[null, null]
	);

	/* ******************************** HANDLERS ******************************* */
	const handleCompanyChange = useCallback(
		(value: string | null) => {
			setSelectedCompany(value);
			if (onCompanyChange) onCompanyChange(value);
		},
		[onCompanyChange]
	);
	const handleProjectChange = useCallback(
		(value: string | null) => {
			setSelectedProject(value);
			if (onProjectChange) onProjectChange(value);
		},
		[onProjectChange]
	);
	const handleContactUs = () => {
		window.open(CONTACT_US_INUK_URL, '_blank');
	};

	const handleOnChangeDate = (value: any) => {
		setSelectedDates(value);
		onDateChange(value);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box size="100%" className={classes.container}>
			<Flex
				direction={{ sm: 'row' }}
				align={{ sm: 'center' }}
				gap={{ base: '16px', sm: '16px' }}
				justify={{
					base: 'space-between',
					sm: 'space-between',
				}}
				pl={'20px'}
			>
				<Title
					c="var(--mantine-color-igluPrimary-9)"
					order={6}
				>
					{t('global.keyFigures')}
				</Title>

				{/* Desktop Filters */}
				<Flex
					direction={{ base: 'column', sm: 'row' }}
					gap={{ base: '16px', sm: '16px' }}
					visibleFrom="md"
				>
					{checkAclFront(
						ACLS.FRONT.DASHBOARD.FILTER_DATE,
						role as ROLES
					) && (
						<DatePickerInput
							value={selectedDates ?? null}
							onChange={handleOnChangeDate}
							size="md"
							placeholder={t('filters.titles.perDate')}
							clearable
							type="range"
							presets={[
								{
									value: [
										dayjs()
											.startOf('week')
											.format('YYYY-MM-DD'),
										dayjs()
											.endOf('week')
											.format('YYYY-MM-DD'),
									],
									label: t('global.thisWeek'),
								},
								{
									value: [
										dayjs()
											.startOf('month')
											.format('YYYY-MM-DD'),
										dayjs()
											.endOf('month')
											.format('YYYY-MM-DD'),
									],
									label: t('global.thisMonth'),
								},
								{
									value: [
										dayjs()
											.startOf('year')
											.format('YYYY-MM-DD'),
										dayjs()
											.endOf('year')
											.format('YYYY-MM-DD'),
									],
									label: t('global.thisYear'),
								},
							]}
						/>
					)}
					{checkAclFront(
						ACLS.FRONT.DASHBOARD.FILTER_COMPANIES,
						role as ROLES
					) &&
						checkRole([
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.BRAND_ADMIN,
							ROLES.BRAND_STAFF,
						]) &&
						companyOptions &&
						Array.isArray(companyOptions) &&
						companyOptions.length > 1 && (
							<Select
								searchable
								data={companyOptions}
								placeholder={t('filters.titles.perBrand')}
								clearable
								size="md"
								leftSection={
									<IconBuilding
										color="var(--mantine-color-igluTextSecondary-9)"
										size={'20px'}
										stroke={1.5}
									/>
								}
								value={selectedCompany}
								onChange={handleCompanyChange}
							/>
						)}
					{checkAclFront(
						ACLS.FRONT.DASHBOARD.FILTER_PROJECTS,
						role as ROLES
					) &&
						checkRole([
							ROLES.ORGANIZATION_ADMIN,
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
						]) &&
						projectOptions &&
						Array.isArray(projectOptions) &&
						projectOptions.length > 1 && (
							<Select
								searchable
								data={projectOptions}
								placeholder={t(
									'filters.titles.perProject'
								)}
								clearable
								size="md"
								leftSection={
									<IconClipboardList
										color="var(--mantine-color-igluTextSecondary-9)"
										size={'20px'}
										stroke={1.5}
									/>
								}
								value={selectedProject}
								onChange={handleProjectChange}
							/>
						)}
				</Flex>
			</Flex>
			{/* Responsive Filters */}
			<Flex
				direction={{ base: 'column' }}
				gap={{ base: '16px', sm: '16px' }}
				hiddenFrom="md"
			>
				<DatePickerInput
					value={selectedDates ?? null}
					onChange={handleOnChangeDate}
					size="md"
					placeholder={t('filters.titles.perDate')}
					clearable
					type="range"
					presets={[
						{
							value: [
								dayjs()
									.startOf('week')
									.format('YYYY-MM-DD'),
								dayjs()
									.endOf('week')
									.format('YYYY-MM-DD'),
							],
							label: t('global.thisWeek'),
						},
						{
							value: [
								dayjs()
									.startOf('month')
									.format('YYYY-MM-DD'),
								dayjs()
									.endOf('month')
									.format('YYYY-MM-DD'),
							],
							label: t('global.thisMonth'),
						},
						{
							value: [
								dayjs()
									.startOf('year')
									.format('YYYY-MM-DD'),
								dayjs()
									.endOf('year')
									.format('YYYY-MM-DD'),
							],
							label: t('global.thisYear'),
						},
					]}
				/>
				{checkRole([
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
				]) &&
					companyOptions &&
					Array.isArray(companyOptions) &&
					companyOptions.length > 1 && (
						<Select
							searchable
							data={companyOptions}
							placeholder={t('filters.titles.perBrand')}
							clearable
							size="md"
							leftSection={
								<IconBuilding
									color="var(--mantine-color-igluTextSecondary-9)"
									size={'20px'}
									stroke={1.5}
								/>
							}
							value={selectedCompany}
							onChange={handleCompanyChange}
						/>
					)}
				{checkRole([
					ROLES.ORGANIZATION_ADMIN,
					ROLES.PRODUCER_ADMIN,
					ROLES.PRODUCER_STAFF,
				]) &&
					projectOptions &&
					Array.isArray(projectOptions) &&
					projectOptions.length > 1 && (
						<Select
							searchable
							data={projectOptions}
							placeholder={t('filters.titles.perProject')}
							clearable
							size="md"
							leftSection={
								<IconClipboardList
									color="var(--mantine-color-igluPrimary-9)"
									size={'20px'}
									stroke={1.5}
								/>
							}
							value={selectedProject}
							onChange={handleProjectChange}
						/>
					)}
			</Flex>
			{!isLoading ? (
				<Box className={classes.body}>
					{data.map((kpi, index) => {
						return (
							<KpiCard
								withShadow={index == 2}
								key={`${kpi.label}-${kpi.dateRange}`}
								className={
									index !== data.length - 1
										? classes.containerKpiCardWithBorder
										: undefined
								}
								withDate={
									!(
										index == 2 &&
										checkRole([
											ROLES.ORGANIZATION_ADMIN,
											ROLES.PRODUCER_ADMIN,
											ROLES.PRODUCER_STAFF,
										])
									)
								}
								{...kpi}
							/>
						);
					})}
					{/* Request My Carbon Report */}
					{checkRole([
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
						ROLES.BRAND_ADMIN,
						ROLES.BRAND_STAFF,
					]) &&
						checkAclFront(
							ACLS.FRONT.DASHBOARD
								.REQUEST_CARBON_ASSESSMENT,
							role as ROLES
						) && (
							<div
								onClick={handleContactUs}
								className={clsx(
									classes.containerKpi,
									classes.containerKpiBilan
								)}
							>
								<div
									style={{
										display: 'flex',
										flex: '1',
										minWidth: '80%',
										paddingTop: '24px',
										paddingLeft: '24px',
										zIndex: '1',
									}}
								>
									<Title
										w={186}
										c="var(--mantine-color-igluBlueLight-9)"
										order={5}
									>
										{t('global.requestMyCarbonReport')}
									</Title>
								</div>
								<div
									style={{
										display: 'flex',
										height: '100%',
										flex: '1',
										justifyContent: 'flex-end',
										alignItems: 'flex-end',
										minWidth: '20%',
									}}
								>
									<Image
										w={'190px'}
										fit="contain"
										src="https://res.cloudinary.com/inuk/image/upload/v1753719634/bilan-logo_gebj6w.png"
									/>
								</div>
							</div>
						)}
				</Box>
			) : (
				<Box className={classes.body}>
					{/* Affiche 3 skeletons */}
					{Array.from({ length: 4 }).map((_, index) => (
						<Box
							key={index}
							className={clsx(
								classes.containerKpi,
								index !== 2 &&
									classes.containerKpiCardWithBorder
							)}
						>
							<Flex
								direction="column"
								gap="sm"
								style={{ padding: '16px' }}
							>
								<Skeleton circle height={48} width={48} />
								<Skeleton height={50} width={'20vw'} />
								<Skeleton height={16} width="60%" />
							</Flex>
						</Box>
					))}

					{/* (Optionnel) Ne pas afficher le bouton de demande de bilan pendant le chargement */}
				</Box>
			)}
		</Box>
	);
};
